import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../api/firebase';
import {
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Box,
  Breadcrumbs,
  Chip,
  Tabs,
  Tab,
  Container,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Computer as ComputerIcon,
  Engineering as EngineeringIcon,
  Architecture as ArchitectureIcon,
  MenuBook as HumanitiesIcon,
  Gavel as LawIcon,
  School as SchoolIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import DepartmentCard from '../components/DepartmentCard';
import ProfessorSearch from '../components/ProfessorSearch';
import { useLanguage } from '../contexts/LanguageContext';

const UniversityPage = () => {
  const { universityId } = useParams();
  const [searchParams] = useSearchParams();
  const [university, setUniversity] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [lecturerCounts, setLecturerCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const { t, isArabic } = useLanguage();

  const getUniversityName = (englishName) => {
    if (!isArabic) return englishName;
    const translations = {
      'King Fahd University of Petroleum & Minerals': 'جامعة الملك فهد للبترول و المعادن',
      'King Saud University': 'جامعة الملك سعود',
      'Prince Sultan University': 'جامعة الأمير سلطان'
    };
    return translations[englishName] || englishName;
  };

  const getDepartmentName = (englishName) => {
    if (!isArabic) return englishName;
    const translations = {
      'Accounting': 'المحاسبة', 'Aviation Department': 'قسم الطيران',
      'Finance': 'المالية', 'Management': 'الإدارة', 'Marketing': 'التسويق',
      'Interior Design': 'التصميم الداخلي', 'Computer Science': 'علوم الحاسب',
      'Information Systems': 'نظم المعلومات', 'Software Engineering': 'هندسة البرمجيات',
      'Communications and Networks Engineering': 'هندسة الاتصالات والشبكات',
      'Construction Engineering': 'هندسة الإنشاءات',
      'Production and Manufacture Engineering': 'هندسة الإنتاج والتصنيع',
      'General Studies': 'الدراسات العامة', 'Linguistics and Translation': 'اللغويات والترجمة',
      'Mathematics and Sciences': 'الرياضيات والعلوم', 'Law': 'القانون',
      'Architecture': 'العمارة', 'Preparatory Year Program': 'برنامج السنة التحضيرية',
    };
    return translations[englishName] || englishName;
  };

  const collegeConfig = [
    {
      code: 'CBA', name: t('Business Administration', 'إدارة الأعمال'),
      fullName: t('College of Business Administration', 'كلية إدارة الأعمال'),
      icon: BusinessIcon, color: '#2E7D32',
      departments: ['Accounting', 'Finance', 'Management', 'Marketing', 'Aviation Department']
    },
    {
      code: 'CCIS', name: t('Computer Science', 'الحاسب'),
      fullName: t('College of Computer & Information Sciences', 'كلية علوم الحاسب والمعلومات'),
      icon: ComputerIcon, color: '#1565C0',
      departments: ['Computer Science', 'Information Systems', 'Software Engineering']
    },
    {
      code: 'CE', name: t('Engineering', 'الهندسة'),
      fullName: t('College of Engineering', 'كلية الهندسة'),
      icon: EngineeringIcon, color: '#E65100',
      departments: ['Construction Engineering', 'Production and Manufacture Engineering', 'Communications and Networks Engineering']
    },
    {
      code: 'CAD', name: t('Architecture', 'العمارة'),
      fullName: t('College of Architecture & Design', 'كلية الهندسة المعمارية والتصميم'),
      icon: ArchitectureIcon, color: '#7B1FA2',
      departments: ['Architecture', 'Interior Design']
    },
    {
      code: 'CHS', name: t('Humanities', 'الإنسانية'),
      fullName: t('College of Humanities & Sciences', 'كلية العلوم الإنسانية والعلوم'),
      icon: HumanitiesIcon, color: '#00695C',
      departments: ['General Studies', 'Linguistics and Translation', 'Mathematics and Sciences', 'Preparatory Year Program']
    },
    {
      code: 'CL', name: t('Law', 'القانون'),
      fullName: t('College of Law', 'كلية القانون'),
      icon: LawIcon, color: '#AD1457',
      departments: ['Law']
    }
  ];

  const getPSUColleges = () => {
    return collegeConfig.map(college => {
      const collegeDepts = departments.filter(dept => college.departments.includes(dept.name));
      return {
        ...college,
        departments: collegeDepts,
        totalLecturers: collegeDepts.reduce((sum, dept) => sum + (lecturerCounts[dept.id] || 0), 0)
      };
    }).filter(college => college.departments.length > 0);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!universityId) return;

      setLoading(true);
      setError(null);
      try {
        const universityDocRef = doc(db, 'universities', universityId);
        const universitySnap = await getDoc(universityDocRef);
        
        if (universitySnap.exists()) {
          setUniversity({ id: universitySnap.id, ...universitySnap.data() });
        }

        const departmentsQuery = query(collection(db, 'departments'), where('universityId', '==', universityId));
        const deptSnapshot = await getDocs(departmentsQuery);
        const deptsList = deptSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDepartments(deptsList);

        const cacheKey = `prof_counts_${universityId}`;
        const cacheTimeKey = `prof_counts_time_${universityId}`;
        const cachedCounts = localStorage.getItem(cacheKey);
        const cacheTime = localStorage.getItem(cacheTimeKey);
        const ONE_HOUR = 60 * 60 * 1000;
        const now = Date.now();
        const cacheAge = cacheTime ? now - parseInt(cacheTime, 10) : ONE_HOUR + 1;
        
        if (cachedCounts && cacheAge < ONE_HOUR) {
          setLecturerCounts(JSON.parse(cachedCounts));
          setLoading(false);
          return;
        }

        const counts = {};
        const countPromises = deptsList.map(async (dept) => {
          const lecturersQuery = query(collection(db, 'professors'), where('departmentId', '==', dept.id));
          const profSnapshot = await getDocs(lecturersQuery);
          return { deptId: dept.id, count: profSnapshot.docs.length };
        });

        const countResults = await Promise.all(countPromises);
        countResults.forEach(({ deptId, count }) => { counts[deptId] = count; });
        
        localStorage.setItem(cacheKey, JSON.stringify(counts));
        localStorage.setItem(cacheTimeKey, now.toString());
        setLecturerCounts(counts);

      } catch (err) {
        setError('Failed to load university data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [universityId]);

  // Handle college param from URL
  useEffect(() => {
    const collegeParam = searchParams.get('college');
    if (collegeParam) {
      const index = collegeConfig.findIndex(c => c.code === collegeParam);
      if (index !== -1) setSelectedTab(index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return <Container maxWidth="lg" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  const totalLecturers = Object.values(lecturerCounts).reduce((sum, count) => sum + count, 0);
  const isPSU = universityId === 'uni-psu';
  const colleges = isPSU ? getPSUColleges() : [];

  return (
    <Box sx={{ direction: isArabic ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <Box
        sx={{
          backgroundImage: 'url(/images/psu-campus.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          py: { xs: 4, md: 6 },
          mb: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.9) 0%, rgba(59, 130, 246, 0.8) 100%)',
            zIndex: 1,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 2, '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.5)' } }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}>
                {t('Home', 'الرئيسية')}
              </Typography>
            </Link>
            <Typography sx={{ color: 'white' }}>
              {university?.name ? getUniversityName(university.name) : t('University', 'الجامعة')}
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              mb: 2,
            }}
          >
            {getUniversityName(university?.name || 'Prince Sultan University')}
          </Typography>

          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              icon={<SchoolIcon sx={{ color: 'white !important' }} />}
              label={`${colleges.length} ${t('Colleges', 'كليات')}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
            />
            <Chip
              icon={<PeopleIcon sx={{ color: 'white !important' }} />}
              label={`${totalLecturers} ${t('Lecturers', 'محاضرين')}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -2, position: 'relative', zIndex: 3 }}>
        {/* Search Card */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {t('Find a Lecturer', 'ابحث عن محاضر')}
          </Typography>
          <ProfessorSearch universityId={universityId} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {t('Search in English or Arabic', 'ابحث بالإنجليزية أو العربية')}
          </Typography>
        </Paper>

        {/* College Tabs */}
        {isPSU && colleges.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'grey.200',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Tabs
                value={selectedTab}
                onChange={(e, newValue) => setSelectedTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    minHeight: 64,
                    px: 3,
                  },
                  '& .Mui-selected': {
                    color: colleges[selectedTab]?.color || 'primary.main',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: colleges[selectedTab]?.color || 'primary.main',
                    height: 3,
                  },
                }}
              >
                {colleges.map((college, index) => {
                  const IconComponent = college.icon;
                  return (
                    <Tab
                      key={college.code}
                      icon={<IconComponent sx={{ fontSize: 24 }} />}
                      iconPosition="start"
                      label={
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {college.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {college.totalLecturers} {t('lecturers', 'محاضر')}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: 'white',
                        },
                      }}
                    />
                  );
                })}
              </Tabs>
            </Box>

            {/* Tab Content */}
            {colleges.map((college, index) => (
              <Box
                key={college.code}
                role="tabpanel"
                hidden={selectedTab !== index}
                sx={{ p: 3 }}
              >
                {selectedTab === index && (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                        {college.fullName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {college.departments.length} {t('departments', 'أقسام')} • {college.totalLecturers} {t('lecturers', 'محاضرين')}
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      {college.departments.map((dept) => (
                        <Grid item xs={12} sm={6} md={4} key={dept.id}>
                          <DepartmentCard 
                            department={{...dept, name: getDepartmentName(dept.name)}} 
                            universityId={universityId}
                            lecturerCount={lecturerCounts[dept.id] || 0}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </Box>
            ))}
          </Paper>
        )}

        {/* Non-PSU departments */}
        {!isPSU && departments.length > 0 && (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              {t('Departments', 'الأقسام')}
            </Typography>
            <Grid container spacing={3}>
              {departments.map((dept) => (
                <Grid item xs={12} sm={6} md={4} key={dept.id}>
                  <DepartmentCard 
                    department={{...dept, name: getDepartmentName(dept.name)}} 
                    universityId={universityId}
                    lecturerCount={lecturerCounts[dept.id] || 0}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default UniversityPage;
